package floris.graphics.backoffice.pages.admin.contributors

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.core.constants.Res
import floris.graphics.core.navigation.Navigation
import com.materialdesignsystem.components.widgets.DsCard
import com.materialdesignsystem.components.widgets.DsEmptyScreen
import com.materialdesignsystem.components.widgets.DsSpinner
import com.materialdesignsystem.components.widgets.SpinnerSize
import com.materialdesignsystem.extensions.maxLines
import com.materialdesignsystem.toColorScheme
import floris.graphics.models.domain.models.Contributor
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A

@Page("/admin/contributors")
@Composable
fun ContributorsPage() {
    val context = rememberPageContext()

    val viewModel = remember { ContributorsViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is ContributorsUiEvent.Navigate -> context.router.navigateTo(it.route)
            }
        }
    }

    ContributorsContent(
        state = state,
        onEvent = viewModel::onEvent
    )
}

@Composable
private fun ContributorsContent(
    state: ContributorsState,
    onEvent: (ContributorsEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        title = "Contributeurs",
        fabOnClick = { onEvent(ContributorsEvent.Navigate(Navigation.Screen.Admin.Contributors.Manage().route)) }
    ) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Large)
            }
            return@PageLayout
        }

        if (!state.isLoading && state.contributors.isEmpty()) {
            DsEmptyScreen(
                title = "Geen contributeurs gevonden",
                description = "Maak jou eerste contributeur aan om deze daarna hier terug te vinden!",
                actionButtonText = "Voeg een nieuwe contributeur toe",
                onActionButtonClick = { onEvent(ContributorsEvent.Navigate(Navigation.Screen.Admin.Contributors.Manage().route)) }
            )
            return@PageLayout
        }

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px)
                .gap(
                    rowGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px,
                    columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
                ),
            numColumns = numColumns(base = 1, sm = 2, xl = 3)
        ) {
            state.contributors.forEach { contributor ->
                A(
                    href = Navigation.Screen.Admin.Contributors.Manage(contributor.id).route,
                    attrs = Modifier
                        .textDecorationLine(TextDecorationLine.None)
                        .toAttrs()
                ) {
                    DsCard(
                        content = {
                            ContributorCardContent(contributor = contributor)
                        }
                    )
                }
            }
        }
    }
}

@Suppress("MagicNumber")
@Composable
fun ContributorCardContent(
    contributor: Contributor,
    titleMaxLength: Int = 2,
) {
    val colorScheme = ColorMode.current.toColorScheme

    Image(
        modifier = Modifier
            .fillMaxWidth()
            .aspectRatio(4, 2)
            .objectFit(ObjectFit.Contain)
            .attrsModifier {
                attr("onerror", "this.onerror=null; this.src='${Res.Image.logo}'")
            },
        src = contributor.logoMediaFile?.url?.toString() ?: Res.Image.logo, // TODO [#28]: create fallback image!!
        description = "Post Thumbnail Image"
    )

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 8.px),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .color(colorScheme.onBackground)
                .fontSize(20.px)
                .fontWeight(FontWeight.Bold)
                .textOverflow(TextOverflow.Ellipsis)
                .overflow(Overflow.Hidden)
                .maxLines(titleMaxLength),
            text = contributor.name
        )

        if (!contributor.bio.isNullOrBlank()) {
            SpanText(
                modifier = Modifier
                    .fillMaxWidth()
                    .color(colorScheme.onBackground)
                    .fontSize(16.px)
                    .textOverflow(TextOverflow.Ellipsis)
                    .overflow(Overflow.Hidden)
                    .maxLines(3),
                text = contributor.bio ?: "No bio available"
            )
        }
    }
}
